import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import {
  processAndUploadStrip,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

interface UserContext {
  user?: {
    email: string
  }
}

const Filter: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    userPreferences,
    setSessionState,
    logActivity,
    id
  } = useSessionStateStore()
  const { filters, getFrame, frames } = useConfigStore()
  const { downloadLocal } = useSettingsStore()

  const [selectedFilterId, setSelectedFilterId] = useState<string>(
    userPreferences.filterId || filters[0]._id
  )
  const [selectedPreviewMode, setSelectedPreviewMode] = useState<'photos' | 'gif'>('photos')
  const [isProcessingLocked, setIsProcessingLocked] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const selectedFrame = getFrame(userPreferences.frameId || frames[0]._id) || null
  const frameConfig = getFrameConfig(selectedFrame)

  // Get captured photos for preview
  const capturedPhotos = gallery.capturedItems
    .filter((item) => item.photoUrl)
    .map((item) => item.photoUrl)

  // Get first photo for filter preview
  const firstPhoto = capturedPhotos[0] || ''

  // Get filtered photos from cache or fallback to original
  const getPhotosForFilter = (filterId: string): string[] => {
    const filterCache = gallery.filterCache[filterId]
    if (filterCache?.photos && filterCache.photos.length === capturedPhotos.length) {
      return filterCache.photos
    }

    // Fallback: try to get individual filtered versions
    const fallbackPhotos = gallery.capturedItems
      .filter((item) => item.photoUrl)
      .map((item) => item.filteredVersions?.[filterId] || item.photoUrl)

    return fallbackPhotos
  }

  // For left filter preview: get the first filtered photo (if available)
  const getFirstFilteredPhoto = (filterId: string): string | undefined => {
    const firstItem = gallery.capturedItems.find((item) => item.photoUrl)
    return firstItem?.filteredVersions?.[filterId] || firstItem?.photoUrl
  }

  // Check if filter is ready (all photos processed)
  const isFilterReady = (filterId: string): boolean => {
    const filterCache = gallery.filterCache[filterId]
    if (filterCache?.isComplete) return true

    // Check if all photos have this filter applied individually
    const processedCount = gallery.capturedItems.filter(
      (item) => item.photoUrl && item.filteredVersions?.[filterId]
    ).length

    return processedCount === capturedPhotos.length
  }

  // Update session state when filter changes
  useEffect(() => {
    setSessionState({
      userPreferences: {
        filterId: selectedFilterId
      }
    })
  }, [selectedFilterId])

  // Reset processing lock when component unmounts
  useEffect(() => {
    return (): void => {
      setIsProcessingLocked(false)
    }
  }, [])

  const handleFilterSelect = (filterId: string): void => {
    setSelectedFilterId(filterId)
    logActivity(`Filter selected: ${filters.find((f) => f._id === filterId)?.name || filterId}`)
  }

  // Only generate real GIF on Next
  const handleNext = async (): Promise<void> => {
    logActivity('Proceeding to result page')

    // Set processing states
    setIsProcessingLocked(true)

    // Timeout fallback in 20s
    const timeout = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn('Processing timed out after 20 seconds')
        resolve()
      }, 20000)
    })

    try {
      // Race between processing and timeout
      await Promise.race([
        (async (): Promise<void> => {
          const stripBlob = await previewRef.current?.exportImage()
          if (!stripBlob) {
            throw new Error('Failed to export strip image')
          }

          const ctx: ProcessingContext = {
            context,
            projectId,
            sessionId: id || '',
            galleryId: galleryKey || gallery.id,
            downloadLocal,
            isOnline: true
          }

          const stripResult = await processAndUploadStrip(stripBlob, ctx)
          const stripLocalUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)

          setSessionState({
            gallery: {
              stripUrl: stripLocalUrl
            }
          })

          const items = useSessionStateStore
            .getState()
            .gallery.capturedItems.filter((item) => item.photoUrl && item.photoUrl.trim() !== '')
            .map((item) => ({ photoUrl: item.photoUrl! }))
          try {
            await processAndUploadPhotos(items, ctx)
            const g = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...g,
                individualPhotosUploaded: true,
                individualPhotosUploadedAt: Date.now()
              }
            })
          } catch (error) {
            console.error('Error uploading photos:', error)
            cogoToast.error('Error uploading photos')
          }

          stateUploader.uploadSessionState()
          navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
        })(),
        timeout
      ])
    } catch (error) {
      console.error('Error in handleNext:', error)
      stateUploader.uploadSessionState()
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    }
    // Don't reset the lock here - let it persist until navigation completes
  }

  // Get the photos and GIF to display based on current filter
  const photosToDisplay = getPhotosForFilter(selectedFilterId)

  // Get live mode video state from gallery
  const liveModeUrl =
    (gallery as { liveModeLocalUrl?: string; liveModeUrl?: string })?.liveModeLocalUrl ||
    gallery?.liveModeUrl ||
    null
  const liveModeProcessing = gallery?.liveModeProcessing ?? false

  return (
    <div className="w-full h-full bg-white flex relative">
      {/* Blur Loading Overlay */}
      {isProcessingLocked && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/90 px-8 py-6 rounded-2xl shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blueGrayYotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing...</h2>
              <p className="text-gray-600">Please wait while we process your photos</p>
            </div>
          </div>
        </div>
      )}

      {/* Left Panel - Filter List (always takes space, but content is conditionally visible) */}
      <div
        className={`w-1/4 p-8 overflow-hidden ${isProcessingLocked ? 'pointer-events-none' : ''}`}
      >
        <div
          className={`h-full flex flex-col justify-between transition-opacity duration-300 ${selectedPreviewMode === 'photos' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          {filters.map((filter) => {
            const filterReady = isFilterReady(filter._id)

            return (
              <button
                key={filter._id}
                onClick={() => handleFilterSelect(filter._id)}
                className={`relative group transition-all duration-300 w-full h-1/4 mb-4 last:mb-0 ${
                  selectedFilterId === filter._id ? 'scale-105 box-shadow-xl' : 'hover:scale-100'
                }`}
              >
                <div
                  className={`relative overflow-hidden rounded-lg border-3 transition-all duration-300 w-full h-full`}
                >
                  {/* Filter Placeholder Background - Smaller Square */}
                  <div className="w-full h-full flex items-center justify-center relative">
                    <img
                      src="/assets/yotta/filter-placeholder.png"
                      alt="Filter Background"
                      className="h-full object-cover"
                    />

                    {/* Overlay with first photo and filter applied - Bigger Centered Square */}
                    {firstPhoto && (
                      <div className="absolute inset-0 flex items-center justify-center p-2">
                        <div className="w-40 h-40 overflow-hidden border-shadow-lg mr-2 mb-4">
                          <img
                            src={getFirstFilteredPhoto(filter._id)}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            crossOrigin="anonymous"
                          />
                        </div>
                      </div>
                    )}

                    {/* Selection Overlay */}
                    {selectedFilterId === filter._id && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full p-1.5">
                          <svg
                            className="w-4 h-4 text-blueGrayYotta"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Processing Indicator */}
                    {!filterReady && (
                      <div className="absolute top-2 right-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse shadow-md"></div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Center Panel - Preview (always takes same space) */}
      <div
        className={`flex-1 flex items-center justify-center p-8 ${isProcessingLocked ? 'pointer-events-none' : ''}`}
      >
        <div className="w-full max-w-lg">
          {selectedPreviewMode === 'photos' ? (
            <PrintPreview
              ref={previewRef}
              frameConfig={frameConfig}
              selectedPhotos={photosToDisplay}
              className="w-full shadow-xl"
              sessionId={galleryKey || gallery.id}
              isInteractive={false}
            />
          ) : (
            <div className="w-full aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden shadow-xl flex items-center justify-center">
              {liveModeUrl ? (
                <video
                  src={liveModeUrl}
                  autoPlay
                  loop
                  controls
                  className="w-full h-full object-contain"
                />
              ) : liveModeProcessing ? (
                <div className="text-gray-500 text-lg">
                  <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto mb-2"></div>
                  Processing Live Mode...
                </div>
              ) : (
                <div className="text-gray-500 text-lg">No Live Mode video available</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Mode Selection (always takes same space) */}
      <div className="w-1/4 p-8 flex flex-col items-center justify-center">
        <div className="space-y-8">
          {/* Photos Button */}
          <button
            onClick={() => setSelectedPreviewMode('photos')}
            className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-lg transition-all duration-300 shadow-lg ${
              selectedPreviewMode === 'photos'
                ? 'bg-blueGrayYotta scale-110'
                : 'bg-gray-400 hover:bg-gray-500 hover:scale-105'
            }`}
          >
            Photos
          </button>

          {/* Live Mode Button */}
          <button
            onClick={() => setSelectedPreviewMode('gif')}
            className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-lg transition-all duration-300 shadow-lg ${
              selectedPreviewMode === 'gif'
                ? 'bg-blueGrayYotta scale-110'
                : 'bg-gray-400 hover:bg-gray-500 hover:scale-105'
            }`}
          >
            Live Mode
          </button>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={isProcessingLocked}
            className={`w-20 h-20 rounded-full text-white font-bold text-lg transition-all duration-300 shadow-lg ${
              isProcessingLocked
                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                : 'bg-blueGrayYotta hover:bg-opacity-90 hover:scale-105'
            }`}
          >
            {isProcessingLocked ? '...' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Filter
